/**
 * Wave CF-P — platform admin chooses which blocks of the court-form library a
 * firm may use. Enterprise = everything, Basic = Federal only (both fixed by
 * plan); Professional = Federal + one state; Custom = any combination.
 * Declared at module scope (standing React rule).
 */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { getAuthToken } from '../../lib/authToken';
import { getApiErrorMessage, toUserErrorMessage } from '../../utils/apiError';

type Ref = { key: string; label: string };
interface Props {
  tenant: { id: string; name: string; plan: string } | null;
  onClose: () => void;
  onSaved: () => void;
}

const TenantCourtFormAccessDialog: React.FC<Props> = ({ tenant, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [plan, setPlan] = useState('');
  const [editable, setEditable] = useState(false);
  const [federal, setFederal] = useState(true);
  const [scope, setScope] = useState<'none' | 'some' | 'all'>('none');
  const [states, setStates] = useState<string[]>([]);
  const [options, setOptions] = useState<Ref[]>([]);

  useEffect(() => {
    if (!tenant) return undefined;
    let live = true;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(`/api/admin/super/court-forms/access/${tenant.id}`, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
        if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Unable to load court form access'));
        const { data } = await res.json();
        if (!live) return;
        const a = data.access;
        setPlan(a.plan);
        setEditable(a.editable);
        setFederal(a.federal);
        setScope(a.allStates ? 'all' : a.states.length ? 'some' : 'none');
        setStates(a.states);
        setOptions((data.jurisdictions as Ref[]).filter((j) => j.key !== 'US-FED'));
      } catch (err) {
        toast.error(toUserErrorMessage(err, 'Unable to load court form access'));
        onClose();
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [tenant, onClose]);

  const professional = plan === 'professional';
  const custom = plan === 'custom';

  const save = async () => {
    if (!tenant) return;
    setSaving(true);
    try {
      const body = professional
        ? { states }
        : { federal, allStates: scope === 'all', states: scope === 'some' ? states : [] };
      const res = await fetch(`/api/admin/super/court-forms/access/${tenant.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Unable to save court form access'));
      toast.success(`Court form access updated for ${tenant.name}`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(toUserErrorMessage(err, 'Unable to save court form access'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={Boolean(tenant)} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Court form access — {tenant?.name}</DialogTitle>
      <DialogContent dividers>
        {loading && <CircularProgress size={28} />}
        {!loading && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Plan: <strong style={{ textTransform: 'capitalize' }}>{plan}</strong>. To change the plan itself use Edit contract.
            </Typography>
            {plan === 'enterprise' && <Alert severity="success">Enterprise includes every form in the library — Federal and all states. Nothing to set.</Alert>}
            {plan === 'basic' && <Alert severity="info">Basic includes Federal forms only. Move the firm to Professional, Enterprise or Custom to add state forms.</Alert>}
            {professional && (
              <>
                <Alert severity="info">Professional includes all Federal forms plus one state.</Alert>
                <Autocomplete
                  options={options}
                  value={options.find((o) => o.key === states[0]) || null}
                  onChange={(_, v) => setStates(v ? [v.key] : [])}
                  getOptionLabel={(o) => `${o.label} (${o.key})`}
                  isOptionEqualToValue={(a, b) => a.key === b.key}
                  renderInput={(params) => <TextField {...params} label="State included" placeholder="No state chosen yet" />}
                />
              </>
            )}
            {custom && (
              <>
                <FormControlLabel control={<Checkbox checked={federal} onChange={(e) => setFederal(e.target.checked)} />} label="Federal forms (USCIS / EOIR)" />
                <RadioGroup value={scope} onChange={(e) => setScope(e.target.value as any)}>
                  <FormControlLabel value="none" control={<Radio />} label="No state forms" />
                  <FormControlLabel value="some" control={<Radio />} label="Selected states" />
                  <FormControlLabel value="all" control={<Radio />} label="All states" />
                </RadioGroup>
                {scope === 'some' && (
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={options}
                    value={options.filter((o) => states.includes(o.key))}
                    onChange={(_, v) => setStates(v.map((o) => o.key))}
                    getOptionLabel={(o) => `${o.label} (${o.key})`}
                    isOptionEqualToValue={(a, b) => a.key === b.key}
                    renderInput={(params) => <TextField {...params} label="States included" />}
                  />
                )}
              </>
            )}
            {editable && (
              <Typography variant="caption" color="text.secondary">
                Removing a block hides those forms from the firm straight away. Forms already added to matters stay there as documents.
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>{editable ? 'Cancel' : 'Close'}</Button>
        {editable && (
          <Button variant="contained" onClick={save} disabled={saving || loading || (custom && scope === 'some' && !states.length)}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TenantCourtFormAccessDialog;
